import { PlusOutlined } from "@ant-design/icons"
import { Button, Empty, Skeleton } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import "react-grid-layout/css/styles.css"
import ReactGridLayout, {
  type Layout,
  WidthProvider
} from "react-grid-layout/legacy"
import { useTranslation } from "react-i18next"

import type {
  WidgetInstance,
  WidgetType
} from "../../../../components/widgets/types.ts"
import { WidgetExpandModal } from "../../../../components/widgets/WidgetExpandModal.tsx"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import {
  ENGAGEMENT_METRIC,
  type EngagementHandlers,
  prefixFilterParams,
  useEngagementWidgetHost
} from "../../../DashboardPage/hooks/useEngagementWidgetHost.tsx"
import { postResolveWidget } from "../../api/services.ts"
import { buildWidgetFilterPayload } from "../../helpers/reportWidgetFilters.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { indexCatalog } from "../../../DashboardPage/components/Portfolio/widgetsResponseAdapter.ts"
import type { CatalogWidgetSpec } from "../../../DashboardPage/redux/widgets.types.ts"
import {
  indexCatalogBySpecKey,
  indexRestrictedBySpecKey
} from "../../helpers/reportCatalog.ts"
import {
  GRID_COLUMNS,
  addWidget,
  applyLayoutChange,
  duplicateWidget,
  minSizeFor,
  removeWidget,
  resolveEffectiveCompare,
  setWidgetCompare,
  setWidgetFilters,
  setWidgetSize,
  setWidgetTitle,
  setWidgetVisualisation,
  toggleCollapseWidget,
  usedSpecKeys
} from "../../helpers/reportDefinition.ts"
import {
  adaptReportWidget,
  buildInstanceConfig,
  rendersCompareRow
} from "../../helpers/reportWidgetAdapter.ts"
import { buildReportResolveParams } from "../../helpers/reportResolveParams.ts"
import {
  reportCanvasApplyDefinition,
  reportCanvasSetConfigWidgetId,
  reportCanvasSetLibraryOpen
} from "../../redux/reportCanvas.slice.ts"
import {
  fetchReportCatalog,
  resolveReportWidget
} from "../../redux/reportCanvas.thunks.ts"
import type {
  ChartVisualisation,
  WidgetPayload
} from "../../../../components/widgets/types.ts"
import type {
  ReportCompareOverride,
  ReportCompareValue,
  ReportDefinitionV1,
  ReportWidgetEntry,
  ReportWidgetFilters,
  WidgetSizePreset
} from "../../types/canvas.types.ts"
import {
  ReportCanvasWidgetTile,
  type ReportTileState
} from "./ReportCanvasWidgetTile.tsx"
import { ReportWidgetConfigDrawer } from "./ReportWidgetConfigDrawer.tsx"
import { ReportWidgetLibraryDrawer } from "./ReportWidgetLibraryDrawer.tsx"

const GridLayoutWithWidth = WidthProvider(ReactGridLayout)

// The catalog's spec.type is a plain string from the API; the registry only ever
// emits values from WidgetType, so this cast is safe.
const specWidgetType = (type: string | undefined): WidgetType | undefined =>
  type as WidgetType | undefined

const ROW_HEIGHT = 32
const GRID_MARGIN: [number, number] = [12, 12]
const PLACEHOLDER_MIN_HEIGHT = 4

const compareLabelFor = (
  compare: ReportCompareValue,
  t: (key: string) => string
): string | undefined => {
  if (compare === "previous_period") return t("reports.compare.previousPeriod")
  if (compare === "same_period_last_year")
    return t("reports.compare.samePeriodLastYear")
  return undefined
}

// Which visualisation options a chart widget offers depends on the shape the
// backend resolved: time series can switch line/bar/area, a project breakdown is
// bar-only, and categorical (donut) charts expose no switch.
const visualisationOptionsFor = (
  type: string | undefined,
  payload: WidgetPayload | null
): ChartVisualisation[] | null => {
  if (type !== "chart") return null
  const chart = payload?.chart
  if (chart?.trend || chart?.cashFlow) return ["line", "bar", "area"]
  if (chart?.breakdown) return ["bar"]
  return null
}

interface ReportCanvasProps {
  readOnly?: boolean
  hidePanels?: boolean
  selectable?: boolean
}

export const ReportCanvas = ({
  readOnly = false,
  hidePanels = false,
  selectable = false
}: ReportCanvasProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const catalog = useAppSelector((s) => s.reportCanvas.catalog)
  const catalogStatus = useAppSelector((s) => s.reportCanvas.catalogStatus)
  const resolved = useAppSelector((s) => s.reportCanvas.resolved)
  const engagementHost = useEngagementWidgetHost()
  const libraryOpen = useAppSelector((s) => s.reportCanvas.libraryOpen)
  const configWidgetId = useAppSelector((s) => s.reportCanvas.configWidgetId)
  const refreshSeq = useAppSelector((s) => s.reportCanvas.refreshSeq)

  useEffect(() => {
    if (catalogStatus === "idle") void dispatch(fetchReportCatalog())
  }, [dispatch, catalogStatus])

  const specIndex = useMemo(() => indexCatalog(catalog), [catalog])
  const bySpecKey = useMemo(() => indexCatalogBySpecKey(catalog), [catalog])
  const restrictedBySpecKey = useMemo(
    () => indexRestrictedBySpecKey(catalog),
    [catalog]
  )

  const requestedSignatures = useRef<Record<string, string>>({})
  // Per-widget refreshes, so a refreshed Leads Added vs. Engaged table gets a
  // new loader and reads its spans again.
  const [widgetRefreshes, setWidgetRefreshes] = useState<
    Record<string, number>
  >({})
  const engagementHandlers = useRef(
    new Map<string, { key: string; handlers: EngagementHandlers }>()
  )

  /** The table's loader and drill, bound to the report's filters and this
   *  widget's own — the same ones its figures were resolved under. Cached per
   *  widget so the table keeps its spans until one of those changes. */
  const engagementHandlersFor = (
    widget: ReportWidgetEntry,
    spec: CatalogWidgetSpec | undefined
  ): EngagementHandlers | undefined => {
    if (!definition || spec?.metric !== ENGAGEMENT_METRIC) return undefined
    const config = buildInstanceConfig(
      spec,
      widget.filters,
      widget.visualisation
    )
    const reportFilters = buildWidgetFilterPayload(definition.filters)
    const key = JSON.stringify([
      config,
      reportFilters,
      refreshSeq,
      widgetRefreshes[widget.id] ?? 0
    ])
    const cached = engagementHandlers.current.get(widget.id)
    if (cached?.key === key) return cached.handlers
    const handlers = engagementHost.bind({
      resolve: (span) =>
        postResolveWidget(
          { type: spec.type, dataSource: spec.data_source, config },
          {
            ...reportFilters,
            window: "custom",
            start: span.start ?? "",
            end: span.end ?? ""
          }
        ),
      drillFilters: {
        ...prefixFilterParams("dash_", reportFilters),
        ...prefixFilterParams(
          "wdash_",
          buildWidgetFilterPayload(widget.filters)
        )
      }
    })
    engagementHandlers.current.set(widget.id, { key, handlers })
    return handlers
  }
  const lastRefreshSeq = useRef(refreshSeq)
  const [expandedWidgetId, setExpandedWidgetId] = useState<string | null>(null)

  useEffect(() => {
    if (!definition || !catalog) return
    if (lastRefreshSeq.current !== refreshSeq) {
      lastRefreshSeq.current = refreshSeq
      requestedSignatures.current = {}
    }
    const reportCompare = definition.compare ?? null
    for (const widget of definition.widgets) {
      const spec = bySpecKey.get(widget.spec)
      if (!spec) continue
      const effectiveCompare = resolveEffectiveCompare(
        widget.compare,
        reportCompare
      )
      const params = buildReportResolveParams({
        window: definition.window,
        compare: effectiveCompare,
        filters: definition.filters
      })
      const config = buildInstanceConfig(
        spec,
        widget.filters,
        widget.visualisation
      )
      const signature = JSON.stringify([widget.spec, config, params])
      if (requestedSignatures.current[widget.id] === signature) continue
      requestedSignatures.current[widget.id] = signature
      void dispatch(
        resolveReportWidget({
          widgetId: widget.id,
          type: spec.type,
          dataSource: spec.data_source,
          config,
          params
        })
      )
    }
  }, [definition, catalog, bySpecKey, dispatch, refreshSeq])

  const currency = useMemo(() => {
    for (const key of Object.keys(resolved)) {
      const currencyValue = (
        resolved[key].resolved?.payload as { currency?: string } | undefined
      )?.currency
      if (currencyValue) return currencyValue
    }
    return undefined
  }, [resolved])

  const addedSpecKeys = useMemo(() => usedSpecKeys(definition), [definition])

  const applyDefinition = (next: ReportDefinitionV1): void => {
    dispatch(reportCanvasApplyDefinition(next))
  }

  const handleAddWidget = (spec: CatalogWidgetSpec) => {
    if (!definition) return
    applyDefinition(
      addWidget(definition, {
        id: crypto.randomUUID(),
        spec: spec.key,
        type: specWidgetType(spec.type)
      })
    )
  }

  const handleDuplicate = (id: string) => {
    if (!definition) return
    const widget = definition.widgets.find((w) => w.id === id)
    const spec = widget ? bySpecKey.get(widget.spec) : undefined
    applyDefinition(
      duplicateWidget(
        definition,
        id,
        crypto.randomUUID(),
        specWidgetType(spec?.type)
      )
    )
  }

  const handleToggleCollapse = (id: string) => {
    if (!definition) return
    const widget = definition.widgets.find((w) => w.id === id)
    const spec = widget ? bySpecKey.get(widget.spec) : undefined
    applyDefinition(
      toggleCollapseWidget(definition, id, specWidgetType(spec?.type))
    )
  }

  const handleRemove = (id: string) => {
    if (!definition) return
    applyDefinition(removeWidget(definition, id))
  }

  const handleChangeTitle = (id: string, title: string | undefined) => {
    if (!definition) return
    applyDefinition(setWidgetTitle(definition, id, title))
  }

  const handleChangeSize = (id: string, size: WidgetSizePreset) => {
    if (!definition) return
    const widget = definition.widgets.find((w) => w.id === id)
    const spec = widget ? bySpecKey.get(widget.spec) : undefined
    applyDefinition(
      setWidgetSize(definition, id, size, specWidgetType(spec?.type))
    )
  }

  const handleChangeFilters = (
    id: string,
    filters: ReportWidgetFilters | undefined
  ) => {
    if (!definition) return
    applyDefinition(setWidgetFilters(definition, id, filters))
  }

  const handleChangeCompare = (id: string, compare: ReportCompareOverride) => {
    if (!definition) return
    applyDefinition(setWidgetCompare(definition, id, compare))
  }

  const handleChangeVisualisation = (
    id: string,
    visualisation: ChartVisualisation | undefined
  ) => {
    if (!definition) return
    applyDefinition(setWidgetVisualisation(definition, id, visualisation))
  }

  const handleRefresh = (id: string) => {
    setWidgetRefreshes((previous) => ({
      ...previous,
      [id]: (previous[id] ?? 0) + 1
    }))
    const widget = definition?.widgets.find((w) => w.id === id)
    const spec = widget ? bySpecKey.get(widget.spec) : undefined
    if (!widget || !spec || !definition) return
    const effectiveCompare = resolveEffectiveCompare(
      widget.compare,
      definition.compare ?? null
    )
    void dispatch(
      resolveReportWidget({
        widgetId: id,
        type: spec.type,
        dataSource: spec.data_source,
        config: buildInstanceConfig(spec, widget.filters, widget.visualisation),
        params: buildReportResolveParams({
          window: definition.window,
          compare: effectiveCompare,
          filters: definition.filters
        })
      })
    )
  }

  const commitLayout = (layout: Layout) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(applyLayoutChange(definition, layout)))
  }

  const expandedEntry = expandedWidgetId
    ? (definition?.widgets.find((w) => w.id === expandedWidgetId) ?? null)
    : null
  const expandedSpec = expandedEntry
    ? bySpecKey.get(expandedEntry.spec)
    : undefined
  const expandedResolved = expandedWidgetId
    ? resolved[expandedWidgetId]
    : undefined
  const expandedAdapted =
    expandedEntry &&
    expandedSpec &&
    expandedResolved?.status === "ready" &&
    expandedResolved.resolved
      ? adaptReportWidget(
          expandedResolved.resolved,
          expandedSpec,
          specIndex,
          expandedEntry.title,
          currency,
          compareLabelFor(
            resolveEffectiveCompare(
              expandedEntry.compare,
              definition?.compare ?? null
            ),
            t
          )
        )
      : null

  const configWidget = configWidgetId
    ? (definition?.widgets.find((w) => w.id === configWidgetId) ?? null)
    : null
  const configSpec = configWidget ? bySpecKey.get(configWidget.spec) : undefined
  const configVisualisationOptions = configWidget
    ? visualisationOptionsFor(
        configSpec?.type,
        resolved[configWidget.id]?.resolved
          ? adaptReportWidget(
              resolved[configWidget.id].resolved!,
              configSpec,
              specIndex,
              configWidget.title,
              currency
            ).payload
          : null
      )
    : null

  if (!definition || catalogStatus === "loading" || catalogStatus === "idle") {
    return <Skeleton active paragraph={{ rows: 4 }} />
  }

  const gridLayout: Layout = definition.layout.map((item) => {
    const widget = definition.widgets.find((w) => w.id === item.i)
    const spec = widget ? bySpecKey.get(widget.spec) : undefined
    const comparing =
      resolveEffectiveCompare(widget?.compare, definition.compare ?? null) !==
        null && rendersCompareRow(resolved[item.i]?.resolved ?? null)
    const min = minSizeFor(specWidgetType(spec?.type), comparing)
    // Without a spec the tile falls back to an access placeholder, whose icon and
    // two lines of copy need more rows than the compact tiles a KPI settles for.
    const minH = spec ? min.h : Math.max(min.h, PLACEHOLDER_MIN_HEIGHT)
    return {
      ...item,
      w: Math.max(item.w, min.w),
      h: widget?.collapsed ? item.h : Math.max(item.h, minH),
      minW: min.w,
      minH,
      isResizable: readOnly ? false : !widget?.collapsed
    }
  })

  return (
    <>
      {definition.widgets.length === 0 ? (
        <div className="flex-1 min-h-[50vh] flex flex-col items-center justify-center gap-4">
          <Empty description={t("reports.detail.emptyCanvasTitle")} />
          {!readOnly && !hidePanels && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => dispatch(reportCanvasSetLibraryOpen(true))}
            >
              {t("reports.detail.addWidget")}
            </Button>
          )}
        </div>
      ) : (
        <GridLayoutWithWidth
          className="layout shrink-0"
          layout={gridLayout}
          cols={GRID_COLUMNS}
          rowHeight={ROW_HEIGHT}
          margin={GRID_MARGIN}
          draggableHandle=".rgl-drag-handle"
          isDraggable={!readOnly}
          isResizable={!readOnly}
          compactType="vertical"
          allowOverlap={false}
          preventCollision
          useCSSTransforms
          onDragStop={commitLayout}
          onResizeStop={commitLayout}
        >
          {definition.widgets.map((widgetEntry) => {
            const spec = bySpecKey.get(widgetEntry.spec)
            const engagement = engagementHandlersFor(widgetEntry, spec)
            const restrictedSpec = restrictedBySpecKey.get(widgetEntry.spec)
            const entry = resolved[widgetEntry.id]

            const {
              widget: tileWidget,
              payload: tilePayload,
              state: tileState
            } = !spec
              ? {
                  widget: {
                    id: widgetEntry.id,
                    type: "kpi",
                    title:
                      widgetEntry.title ||
                      restrictedSpec?.title ||
                      t("reports.canvas.widgetUnavailable")
                  } as WidgetInstance,
                  payload: null,
                  state: (restrictedSpec
                    ? "restricted"
                    : "unavailable") as ReportTileState
                }
              : entry?.status === "ready" && entry.resolved
                ? {
                    ...adaptReportWidget(
                      entry.resolved,
                      spec,
                      specIndex,
                      widgetEntry.title,
                      currency,
                      compareLabelFor(
                        resolveEffectiveCompare(
                          widgetEntry.compare,
                          definition.compare ?? null
                        ),
                        t
                      )
                    ),
                    state: "ready" as const
                  }
                : {
                    widget: {
                      id: widgetEntry.id,
                      type: spec?.type ?? "kpi",
                      title: widgetEntry.title || spec?.title
                    } as WidgetInstance,
                    payload: null,
                    state:
                      entry?.status === "error"
                        ? ("error" as const)
                        : ("loading" as const)
                  }

            return (
              <div key={widgetEntry.id}>
                <ReportCanvasWidgetTile
                  widget={tileWidget}
                  payload={tilePayload}
                  state={tileState}
                  currency={currency}
                  collapsed={widgetEntry.collapsed}
                  title={tileWidget.title ?? spec?.title ?? ""}
                  readOnly={readOnly}
                  selectable={selectable}
                  selected={selectable && configWidgetId === widgetEntry.id}
                  onSelect={() =>
                    dispatch(reportCanvasSetConfigWidgetId(widgetEntry.id))
                  }
                  onConfigure={() =>
                    dispatch(reportCanvasSetConfigWidgetId(widgetEntry.id))
                  }
                  onDuplicate={() => handleDuplicate(widgetEntry.id)}
                  onToggleCollapse={() => handleToggleCollapse(widgetEntry.id)}
                  onRemove={() => handleRemove(widgetEntry.id)}
                  onRefresh={() => handleRefresh(widgetEntry.id)}
                  onExpand={() => setExpandedWidgetId(widgetEntry.id)}
                  onEngagementDrill={engagement?.onEngagementDrill}
                  loadEngagementRange={engagement?.loadEngagementRange}
                />
              </div>
            )
          })}
        </GridLayoutWithWidth>
      )}

      <WidgetExpandModal
        widget={expandedAdapted?.widget ?? null}
        payload={expandedAdapted?.payload ?? null}
        currency={currency}
        onClose={() => setExpandedWidgetId(null)}
      />

      {!readOnly && !hidePanels && (
        <>
          <ReportWidgetLibraryDrawer
            open={libraryOpen}
            catalog={catalog}
            loading={false}
            onAdd={handleAddWidget}
            onClose={() => dispatch(reportCanvasSetLibraryOpen(false))}
            addedSpecKeys={addedSpecKeys}
          />

          <ReportWidgetConfigDrawer
            open={configWidgetId !== null}
            widget={configWidget}
            spec={configSpec}
            visualisationOptions={configVisualisationOptions}
            onClose={() => dispatch(reportCanvasSetConfigWidgetId(null))}
            onChangeTitle={handleChangeTitle}
            onChangeSize={handleChangeSize}
            onChangeFilters={handleChangeFilters}
            onChangeCompare={handleChangeCompare}
            onChangeVisualisation={handleChangeVisualisation}
          />
        </>
      )}
      {engagementHost.drillModal}
    </>
  )
}
